import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { pageMeta } from "@/data/mockData";
import { useCaseStudies } from "@/lib/content";
import { stripHtml, truncate } from "@/lib/format";

export default function Results() {
  const { data: caseStudies } = useCaseStudies();
  return (
    <>
      <SEO {...pageMeta.results} />

      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="results-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Proven Results
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Outcomes that matter.
            </h1>
            <p className="mt-7 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Selected anonymised client journeys — each illustrating the challenge, the strategy, and the outcome. Past results do not guarantee future returns.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fafafa] py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {/* BACKEND INTEGRATION: replace `caseStudies` with fetch to /api/case-studies */}
          {caseStudies.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              className="bg-white border border-gray-100 rounded-3xl overflow-hidden hover:shadow-xl transition-all group"
              data-testid={`results-case-${c.id}`}
            >
              <div className="grid lg:grid-cols-[1fr,1.3fr]">
                <div className="overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.headline}
                    loading="lazy"
                    className="w-full h-auto block group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col gap-5">
                  <div>
                    {c.metric && (
                      <div className="inline-block bg-[#fffbea] text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full mb-4">
                        {c.metric}
                      </div>
                    )}
                    <h2 className="font-serif text-2xl md:text-3xl text-[#0a2540] leading-snug">
                      {c.headline}
                    </h2>
                    {c.propertyType && (
                      <p className="text-xs text-slate-500 mt-2 uppercase tracking-widest">{c.propertyType}</p>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed pt-4 border-t border-gray-100">
                    {truncate(stripHtml(c.story), 240)}
                  </p>
                  <Link
                    to={`/results/${c.id}`}
                    data-testid={`results-case-link-${c.id}`}
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#0a2540] link-underline self-start"
                  >
                    Read full case study
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <CTABanner />
    </>
  );
}
